import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useViewport } from "src/contexts";
import {
  CommunityOrLotStepper,
  ConfiguratorMap,
  scrollToStepWrapperElement,
  SkeletonStepper,
  StepWrapper,
} from "src/pages/configurator/components";
import { configurationsLandingPath, createLotsUrl } from "src/RouteUrls";
import { useCurrentUser } from "src/utils/hooks/useCurrentUser";
import { MapMarker, SelectableOptionCard } from "~components";
import { Css } from "~generated/css";
import { UserType } from "~generated/graphqlTypes";
import { getPlanBathroomRangeString, getPlanBedroomRangeString, getPlanSqftRangeString, queryResult } from "~utils";
import { useConfiguratorContext } from "../Configurator.context";
import {
  CommunitiesStep_Community_ProductOfferingFragment,
  CommunitiesStep_CommunityFragment,
  useCommunitiesStep_UserHasConfigsQuery,
  useCommunitiesStepQuery,
} from "./Communities.step.generated";

export function CommunitiesStep() {
  const query = useCommunitiesStepQuery();
  return queryResult(query, {
    data: ({ communities }) => {
      // Only show communities with at least one product offering that is checkout enabled.
      const communitiesWithProductOfferings = communities.entities.filter(
        (c) => !!c.productOfferings?.filter((po) => po.showInCheckout).length,
      );
      return <CommunitiesStepView communities={communitiesWithProductOfferings} />;
    },
    // Note: this query is so quick that we'll probably never see this state actually happen
    loading: () => {
      return (
        <>
          <StepWrapper
            title="Community"
            description="Select a community to view available lots and start your home configuration journey."
            loading
            left={<ConfiguratorMap center="continentalUS" />}
            right={<></>}
          />
          <SkeletonStepper />
        </>
      );
    },
  });
}

function CommunitiesStepView({ communities }: { communities: CommunitiesStep_CommunityFragment[] }) {
  const navigate = useNavigate();
  const { isMobile } = useViewport();
  const [hoveredCommunity, setHoveredCommunity] = useState<string | undefined>();
  const { setCommunityId, communityId } = useConfiguratorContext();

  // Lightweight query to check if the current user has any existing checkout configs.
  // On mobile, we only show the back button if they have configs to go back to.
  const { currentUser } = useCurrentUser();
  const userId = currentUser?.userId;
  const isHomeowner = currentUser?.type === UserType.Homeowner;
  const { data: userConfigsData } = useCommunitiesStep_UserHasConfigsQuery({
    skip: !isMobile || !isHomeowner,
    variables: {
      filter: { userId: userId ? [userId] : [] },
    },
  });
  const hasExistingConfigs = userConfigsData?.checkoutConfigs.entities.nonEmpty;

  const mapMarkers = useMemo(() => {
    return communities
      .filter((c) => c.latitude && c.longitude)
      .map((community) => {
        return {
          id: community.id,
          center: { lat: Number(community.latitude), lng: Number(community.longitude) },
          marker: (
            <MapMarker
              onHoverStart={() => setHoveredCommunity(community.id)}
              onHoverEnd={() => setHoveredCommunity(undefined)}
              onClick={() => {
                setCommunityId(community.id);
                scrollToStepWrapperElement(`${community.id}-card`, isMobile);
              }}
              isActive={communityId === community.id}
              isHovered={hoveredCommunity === community.id}
              data-testid={`mapMarker-${community.id}`}
            />
          ),
        };
      });
  }, [communities, hoveredCommunity, communityId, setCommunityId, isMobile]);

  return (
    <>
      <StepWrapper
        title="Community"
        description="Select a community to view available lots and start your home configuration journey."
        left={<ConfiguratorMap markers={mapMarkers} center="continentalUS" />}
        right={communities.map((community) => (
          <div id={`${community.id}-card`} key={community.id}>
            <SelectableOptionCard
              title={community.name}
              description={<CommunityCardDescription community={community} />}
              selected={communityId === community.id}
              onClick={() => setCommunityId(community.id)}
              onHoverStart={() => setHoveredCommunity(community.id)}
              onHoverEnd={() => setHoveredCommunity(undefined)}
              isHovered={hoveredCommunity === community.id}
            />
          </div>
        ))}
      />
      <CommunityOrLotStepper
        onBack={
          !isMobile || !hasExistingConfigs
            ? undefined
            : () => {
                navigate(configurationsLandingPath);
              }
        }
        onNext={() => {
          communityId && navigate(createLotsUrl(communityId));
        }}
        disabledReasons={!communityId ? ["Please select a community"] : []}
      />
    </>
  );
}

type CommunityProgramDataRange = {
  productOfferings: CommunitiesStep_Community_ProductOfferingFragment[];
};

export function getCommunityProgramDataRange(community: CommunityProgramDataRange) {
  const minProgramData = {
    bedrooms: Math.min(...community.productOfferings.map((po) => po.minProgramData.bedrooms ?? 0)),
    fullBaths: Math.min(...community.productOfferings.map((po) => po.minProgramData.fullBaths ?? 0)),
    halfBaths: Math.min(...community.productOfferings.map((po) => po.minProgramData.halfBaths ?? 0)),
    sellableSqft: Math.min(...community.productOfferings.map((po) => po.minProgramData.sellableSqft ?? 0)),
  };

  const maxProgramData = {
    bedrooms: Math.max(...community.productOfferings.map((po) => po.maxProgramData.bedrooms ?? 0)),
    fullBaths: Math.max(...community.productOfferings.map((po) => po.maxProgramData.fullBaths ?? 0)),
    halfBaths: Math.max(...community.productOfferings.map((po) => po.maxProgramData.halfBaths ?? 0)),
    sellableSqft: Math.max(...community.productOfferings.map((po) => po.maxProgramData.sellableSqft ?? 0)),
  };

  return { minProgramData, maxProgramData };
}

function CommunityCardDescription({ community }: { community: CommunitiesStep_CommunityFragment }) {
  const programData = getCommunityProgramDataRange(community);
  return (
    <>
      {!!community.pricingTeaser && <div css={Css.t14.gray80.$}>{community.pricingTeaser}</div>}
      <div css={Css.df.gap1.aic.t14.gray80.$}>
        <span>{getPlanBedroomRangeString(programData)}</span>
        <span>{getPlanBathroomRangeString(programData)}</span>
        <span>{getPlanSqftRangeString(programData)}</span>
      </div>
    </>
  );
}
